import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EnergyCell } from "../components/fleet/EnergyCell";
import { RegisterStation } from "../components/fleet/RegisterStation";
import { StationCredential } from "../components/fleet/StationCredential";
import { UsageWindowPicker } from "../components/fleet/UsageWindowPicker";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SearchField, SelectField } from "../components/ui/Input";
import { ConnectionStatus, ConnectorStatus, Pill, RegistrationPill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { getPage } from "../lib/api";
import { uptimeFigure } from "../lib/dashboard";
import { connectorLabel } from "../lib/evse";
import { count, isHeartbeatStale, kwh, since } from "../lib/format";
import { useSession } from "../lib/session";
import { connectorTone } from "../lib/status";
import { saleState } from "../lib/tariff";
import type { ChargePoint, ChargePointRow, Depot, ProvisionedStation } from "../lib/types";
import { isUptimeLow } from "../lib/uptime";
import { readUsageWindow, reportingZone } from "../lib/usageWindow";

/**
 * The fleet view. The one page in this build backed entirely by live data.
 *
 * ## Filters live in the URL
 *
 * Depot, status, search and page are all search parameters, so a filtered view is
 * a link an operator can paste into a ticket and a browser back button behaves.
 * Nothing about the filter state is held in component state — there is one source
 * of truth and it is addressable.
 *
 * ## It polls
 *
 * `connected` comes from an in-memory registry (spec 10.3) and changes with no
 * request from this client, so a static fleet table is wrong within seconds of
 * loading. Ten seconds is slow enough to be a rounding error against the 300s
 * heartbeat interval and fast enough that an operator watching a station reboot
 * sees it come back.
 */

const COLUMNS: readonly Column[] = [
  { key: "id", label: "Charge point", width: "19%" },
  { key: "depot", label: "Depot", width: "11%" },
  { key: "connection", label: "Connection", width: "12%" },
  /**
   * The last seven days, beside the live state: "offline now" and "offline a lot"
   * are different problems, and only the second is the station's.
   */
  { key: "uptime", label: "Uptime 7d", numeric: true, width: "10%" },
  { key: "connectors", label: "Connectors" },
  { key: "registration", label: "Boot", width: "9%" },
  /**
   * Whether the station is earning. pay-ui spec §4.2 — the columns behind it have
   * existed since public charging shipped and this table has never shown them, so
   * "is this station on sale, and at what price" was a question only `psql` could
   * answer.
   */
  { key: "sale", label: "Public", width: "12%" },
  { key: "heartbeat", label: "Last heartbeat", numeric: true, width: "13%" },
];

/**
 * Appended only while a window is selected, rather than always present and empty.
 *
 * A permanent column of dashes would cost a tenth of the table's width to say
 * nothing, and — worse — would make an operator who has not chosen a window
 * suspect the stations delivered nothing.
 */
const ENERGY_COLUMN: Column = { key: "energy", label: "Energy", numeric: true, width: "11%" };

const POLL_MS = 10_000;

export function Fleet() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = useSession();

  const [isRegistering, setIsRegistering] = useState(false);
  /**
   * Held in state, not in the URL, and deliberately unlike every filter on this
   * page. A password in a query string would be in the browser's history, in the
   * back button, and in whatever the operator pastes into a ticket next.
   */
  const [provisioned, setProvisioned] = useState<ProvisionedStation | undefined>(undefined);

  const depotId = params.get("depotId") ?? "";
  const status = params.get("status") ?? "";
  const search = params.get("search") ?? "";
  const connected = params.get("connected") ?? "";
  /** `"true"` shows retired stations instead of the working fleet — never both. */
  const hidden = params.get("hidden") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;
  const range = params.get("range") ?? "";
  const fromDate = params.get("fromDate");
  const fromTime = params.get("fromTime");
  const toDate = params.get("toDate");
  const toTime = params.get("toTime");

  const depots = useQuery({
    queryKey: ["depots"],
    queryFn: () => getPage<Depot>("/depots"),
    // Sites do not appear while someone is looking at a table.
    staleTime: 10 * 60_000,
  });

  /**
   * The window, resolved in the depot's calendar rather than the browser's — spec
   * 6.1 draws every boundary there, and a UTC month reports the first seven hours
   * of a Phnom Penh month under the previous one.
   *
   * Memoised on the range and the zone, not recomputed per render, and that is
   * load-bearing twice over: `resolveWindow` reads the clock, so a fresh call
   * every render would produce a new `from`/`to` pair, a new query key, and a
   * refetch on every repaint of a table that already polls.
   */
  const zone = reportingZone(depots.data?.rows ?? [], depotId);
  const windowState = useMemo(
    () => readUsageWindow(range, { fromDate, fromTime, toDate, toTime }, zone),
    [range, fromDate, fromTime, toDate, toTime, zone],
  );

  /**
   * A window only when there is one to send. A custom range with a date still
   * missing resolves to `pending`, and the energy column stays off rather than
   * the fleet list being asked for a half-specified window it would answer with
   * a 400 — one column's worth of missing information taking the whole table
   * down with it.
   */
  const usageWindow = windowState.kind === "resolved" ? windowState.window : undefined;

  const from = usageWindow?.from.toISOString() ?? "";
  const to = usageWindow?.to.toISOString() ?? "";

  const fleet = useQuery({
    queryKey: ["charge-points", { depotId, status, search, connected, hidden, page, from, to }],
    queryFn: () =>
      getPage<ChargePointRow>("/charge-points", {
        query: { depotId, status, search, connected, hidden, page, from, to },
      }),
    refetchInterval: POLL_MS,
    // Keeps the previous page on screen while the next one loads, so paging does
    // not flash a skeleton over a table the operator was reading.
    placeholderData: (previous) => previous,
  });

  const rows = fleet.data?.rows ?? [];
  const columns = usageWindow === undefined ? COLUMNS : [...COLUMNS, ENERGY_COLUMN];

  /**
   * Computed from the page, not from `meta.total`, and labelled as such below.
   * Connection state is only known for rows the server actually returned — there
   * is no fleet-wide "how many are online" figure available without fetching the
   * whole fleet, and inventing one from a page would be a plausible lie.
   */
  const summary = useMemo(() => {
    const now = Date.now();
    return {
      online: rows.filter((row) => row.connected).length,
      faulted: rows.filter((row) =>
        row.connectors.some(
          (connector) => connectorTone(connector.status, connector.errorCode) === "faulted",
        ),
      ).length,
      charging: rows.filter((row) =>
        row.connectors.some((connector) => connector.status === "Charging"),
      ).length,
      stale: rows.filter((row) => !row.connected && isHeartbeatStale(row.lastHeartbeatAt, now))
        .length,
      /**
       * Summed across this page only, like every figure in this masthead. The
       * hint says so: `meta.total` counts stations the page did not return, and a
       * total that silently covered some of them would be the one number on
       * screen an operator might put in a report.
       *
       * For a fleet-wide figure there is the depots table, whose rows are whole
       * sites and are never paginated.
       */
      energyWh: rows.reduce((sum, row) => sum + (row.usage?.energyWh ?? 0), 0),
    };
  }, [rows]);

  /** Writing a filter always resets to page 1: page 4 of a new filter is a blank table. */
  /**
   * A batch, not one key at a time. The date range picker changes both ends in a
   * single click — choosing a new start clears the old end — and two sequential
   * calls would both read this same `params` snapshot, so the second would
   * overwrite the first's write and silently drop it.
   */
  function setFilter(updates: Readonly<Record<string, string>>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value === "") next.delete(key);
      else next.set(key, value);
    }
    next.delete("page");
    setParams(next, { replace: true });
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setParams(next);
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Charge points"
        metrics={
          <>
            <Metric label="On this page" value={count(rows.length)} hint="rows returned" />
            <Metric
              label="Online"
              value={count(summary.online)}
              hint="live websocket"
              tone={summary.online === 0 && rows.length > 0 ? "muted" : "ink"}
            />
            <Metric label="Charging" value={count(summary.charging)} hint="≥1 connector" />
            <Metric
              label="Faulted"
              value={count(summary.faulted)}
              hint="error code raised"
              tone={summary.faulted > 0 ? "signal" : "muted"}
            />
            {usageWindow === undefined ? null : (
              <Metric
                label="Energy"
                value={kwh(summary.energyWh)}
                unit="kWh"
                hint={`${usageWindow.label} · this page`}
              />
            )}
          </>
        }
        freshness={
          <Freshness
            updatedAt={fleet.dataUpdatedAt}
            isFetching={fleet.isFetching}
            intervalMs={POLL_MS}
          />
        }
        lede={
          summary.stale > 0
            ? `${summary.stale} station${summary.stale === 1 ? "" : "s"} on this page have not sent a heartbeat in over 15 minutes and hold no live connection.`
            : "Connection state is read from the live socket registry, so it is current rather than a stored column."
        }
        actions={
          can("charge_points.register") && !isRegistering ? (
            <Button variant="primary" onClick={() => setIsRegistering(true)}>
              Register station
            </Button>
          ) : undefined
        }
      />

      {/*
        The credential outlives the form: submitting closes the form and this is
        the only place the password is ever shown, so it must not be a transient
        part of a component that has just unmounted.
      */}
      {provisioned === undefined ? null : (
        <StationCredential
          station={provisioned}
          heading={`${provisioned.chargePoint.id} registered`}
          onDismiss={() => setProvisioned(undefined)}
        />
      )}

      {isRegistering ? (
        <RegisterStation
          depots={depots.data?.rows ?? []}
          onProvisioned={(station) => {
            setProvisioned(station);
            setIsRegistering(false);
          }}
          onCancel={() => setIsRegistering(false)}
        />
      ) : null}

      <form
        // The filters are already applied on change; submitting is a no-op, and
        // preventing it stops Enter in the search box from reloading the page.
        onSubmit={(event) => event.preventDefault()}
        // Named because the register form carries a "Depot" control of its own, and
        // "the depot filter" and "the depot this station goes in" are different
        // questions. Without names, neither a screen reader nor a test can say which.
        aria-label="Filters"
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-3 lg:grid-cols-6"
      >
        <SearchField
          label="Search"
          placeholder="Charge point id"
          value={search}
          onChange={(event) => setFilter({ search: event.target.value })}
        />
        <SelectField
          label="Depot"
          value={depotId}
          onChange={(event) => setFilter({ depotId: event.target.value })}
        >
          <option value="">All depots</option>
          {(depots.data?.rows ?? []).map((depot) => (
            <option key={depot.id} value={depot.id}>
              {depot.name} · {depot.timezone}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Boot status"
          value={status}
          onChange={(event) => setFilter({ status: event.target.value })}
        >
          <option value="">Any</option>
          <option value="Accepted">Accepted</option>
          <option value="Pending">Pending</option>
          <option value="Rejected">Rejected</option>
        </SelectField>
        <SelectField
          label="Connection"
          value={connected}
          onChange={(event) => setFilter({ connected: event.target.value })}
        >
          <option value="">Any</option>
          <option value="true">Online only</option>
          <option value="false">Not connected</option>
        </SelectField>
        <SelectField
          label="Showing"
          value={hidden}
          onChange={(event) => setFilter({ hidden: event.target.value })}
        >
          <option value="">In service</option>
          <option value="true">Hidden</option>
        </SelectField>
        <UsageWindowPicker
          range={range}
          custom={{ fromDate, fromTime, toDate, toTime }}
          state={windowState}
          zone={zone}
          onChange={setFilter}
        />
      </form>

      {fleet.isError ? (
        <ErrorNotice error={fleet.error} onRetry={() => void fleet.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={columns} />
          {fleet.isPending ? (
            <SkeletonRows rows={8} columns={columns.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <FleetRow
                  key={row.id}
                  row={row}
                  index={index}
                  showEnergy={usageWindow !== undefined}
                  onOpen={() => navigate(`/fleet/${encodeURIComponent(row.id)}`)}
                />
              ))}
            </tbody>
          )}
        </TableFrame>

        {!fleet.isPending && rows.length === 0 ? (
          <EmptyState
            title={
              hidden === "true"
                ? "No charge points are hidden."
                : hasAnyFilter(params)
                  ? "No charge points match these filters."
                  : "No charge points have registered yet."
            }
            detail={
              hidden === "true"
                ? "A station that has charged before is hidden from its page when it is retired. It shows up here, and can be unhidden from the same page."
                : hasAnyFilter(params)
                  ? "Clear a filter to widen the search. Note that the connection filter is applied to the current page only, so a narrow page can hide matches on later pages."
                  : "Register a station to mint the credential it authenticates with. It appears as Pending until it completes its first BootNotification."
            }
            action={
              can("charge_points.register") && !isRegistering && !hasAnyFilter(params) ? (
                <Button variant="primary" onClick={() => setIsRegistering(true)}>
                  Register the first station
                </Button>
              ) : undefined
            }
          />
        ) : null}

        <Pagination meta={fleet.data?.meta} shown={rows.length} onPage={setPage} />
      </div>
    </>
  );
}

function FleetRow({
  row,
  index,
  showEnergy,
  onOpen,
}: {
  readonly row: ChargePointRow;
  readonly index: number;
  readonly showEnergy: boolean;
  readonly onOpen: () => void;
}) {
  const faulted = row.connectors.some(
    (connector) => connectorTone(connector.status, connector.errorCode) === "faulted",
  );
  const stale = !row.connected && isHeartbeatStale(row.lastHeartbeatAt);

  return (
    <TableRow index={index} onActivate={onOpen} isFaulted={faulted}>
      <Cell>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-ink">{row.id}</span>
          {/* Vendor and model only when known — a row of em dashes is noise. */}
          {row.vendor === null && row.model === null ? null : (
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              {[row.vendor, row.model].filter((part) => part !== null).join(" ")}
              {row.firmwareVersion === null ? "" : ` · ${row.firmwareVersion}`}
            </span>
          )}
        </div>
      </Cell>

      <Cell muted>{row.depotName}</Cell>

      <Cell>
        <ConnectionStatus connected={row.connected} stale={stale} />
      </Cell>

      <UptimeCell ratio={row.uptime?.ratio ?? null} />

      <Cell>
        {row.connectors.length === 0 ? (
          <span className="text-ink-faint">No status reported</span>
        ) : (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {row.connectors.map((connector) => (
              <span
                key={`${connector.evseId}/${connector.connectorId}`}
                className="inline-flex items-center gap-1.5"
              >
                <MicroLabel>{connectorLabel(connector, row.connectors)}</MicroLabel>
                <ConnectorStatus status={connector.status} errorCode={connector.errorCode} />
              </span>
            ))}
          </div>
        )}
      </Cell>

      <Cell>
        <RegistrationPill status={row.registrationStatus} />
      </Cell>

      <Cell>
        <SaleCell station={row} />
      </Cell>

      <Cell numeric muted={!stale} className={stale ? "text-signal" : ""}>
        {since(row.lastHeartbeatAt)}
      </Cell>

      {showEnergy ? <EnergyCell usage={row.usage} /> : null}
    </TableRow>
  );
}

/** A dash before any history: a station nobody has measured is not at 0%. */
function UptimeCell({ ratio }: { readonly ratio: number | null }) {
  const low = isUptimeLow(ratio);
  return (
    <Cell numeric muted={!low} className={low ? "text-signal" : ""}>
      {ratio === null ? "—" : `${uptimeFigure(ratio)}%`}
    </Cell>
  );
}

/**
 * On sale, and at what. The price is the point: "public" alone tells an operator
 * the station is selling but not what a driver pays, and the two questions are
 * never asked separately.
 *
 * A station off sale keeps its price on the row, so this deliberately says *Off
 * sale* rather than *Private* once one has been chosen — the first is a switch, the
 * second is a decision about money, and they are different next actions.
 */
function SaleCell({ station }: { readonly station: ChargePoint }) {
  const state = saleState(station);

  if (state.kind === "private") {
    return <span className="text-ink-faint">Private</span>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      <Pill tone={state.kind === "off_sale" ? "idle" : "available"}>
        {state.kind === "off_sale" ? "Off sale" : "On sale"}
      </Pill>
      <span className="text-micro tracking-normal normal-case text-ink-faint">
        {state.price}
        {/* A price retired under a station still selling on it. Nothing prevents
            that — the write path only refuses to *set* a retired price — so the
            table has to be able to say it. */}
        {state.kind === "on_sale_retired" ? " · retired price" : ""}
      </span>
    </div>
  );
}

function hasAnyFilter(params: URLSearchParams): boolean {
  return ["depotId", "status", "search", "connected"].some((key) => (params.get(key) ?? "") !== "");
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
